import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import { 
  Loader2, Package, Calendar, Hash, IndianRupee, CreditCard, 
  ShieldCheck, MessageCircle, ArrowLeft, X, Send, CheckCircle2 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getApiUrl } from '../../apiConfig';

const UserOrders = () => {
  const { user } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Tickets & Chat State
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  const messagesEndRef = useRef(null);

  // Lock body scroll when chat modal is open
  useEffect(() => {
    if (activeTicket) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeTicket]);

  useEffect(() => {
    fetchOrders();
    fetchUserTickets();
  }, [user]);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const emailParam = user?.email ? `email=${encodeURIComponent(user.email)}` : '';
      const phoneParam = user?.phone ? `phone=${encodeURIComponent(user.phone)}` : '';
      const queryStr = [emailParam, phoneParam].filter(Boolean).join('&');

      const targetUrl = getApiUrl(`/api/v1/order/user-orders${queryStr ? '?' + queryStr : ''}`);
      const response = await fetch(targetUrl, {
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await response.json();

      if (data.success) {
        setOrders(data.orders || data.transactions || []);
      }
    } catch (err) {
      console.error('Error fetching user orders:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserTickets = async () => {
    try {
      const emailParam = user?.email ? `email=${encodeURIComponent(user.email)}` : '';
      const userIdVal = user?.id || '0';
      const response = await fetch((window.API_BASE || "") + `/api/users/${userIdVal}/tickets${emailParam ? '?' + emailParam : ''}`, {
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await response.json();
      if (data.success) {
        setTickets(data.tickets || []);
      }
    } catch (err) {
      console.error("Error fetching user tickets:", err);
    }
  };

  // Fetch chat messages for active ticket
  const fetchChatMessages = async (ticketId, isSilent = false) => {
    if (!isSilent) setIsChatLoading(true);
    try {
      const response = await fetch((window.API_BASE || "") + `/api/tickets/${ticketId}/messages`, {
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await response.json();
      if (data.success) {
        setChatMessages(data.messages || []);
        if (data.ticket) {
          setActiveTicket(data.ticket);
        }
      }
    } catch (err) {
      console.error("Error fetching chat messages:", err);
    } finally {
      if (!isSilent) setIsChatLoading(false);
    }
  };

  // Live polling for chat messages every 3 seconds while active
  useEffect(() => {
    if (!activeTicket?.id) return;
    fetchChatMessages(activeTicket.id, false);

    const interval = setInterval(() => {
      fetchChatMessages(activeTicket.id, true);
    }, 3000);

    return () => clearInterval(interval);
  }, [activeTicket?.id]);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (activeTicket) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTicket]);

  const handleOpenOrderChat = async (order) => {
    setIsOpeningChat(true);
    try {
      const orderTxnId = order.transactionId || order.merchant_transaction_id || `ORD-${order.id}`;
      const assessmentDate = order.assessmentDate || order.assessment_date || '';

      // Check among existing tickets
      let matchedTicket = tickets.find(t => {
        if (t.category === 'CAREER_ASSESSMENT') {
          if (t.message && t.message.includes(orderTxnId)) return true;
          if (assessmentDate && (t.assessment_date === assessmentDate || (t.subject && t.subject.includes(assessmentDate)))) return true;
        }
        return false;
      });

      // If not directly matched by txn id, find any career assessment ticket for user
      if (!matchedTicket) {
        matchedTicket = tickets.find(t => t.category === 'CAREER_ASSESSMENT');
      }

      // If still not found, fetch latest tickets from server
      if (!matchedTicket) {
        const emailParam = user?.email ? `email=${encodeURIComponent(user.email)}` : '';
        const userIdVal = user?.id || '0';
        const res = await fetch((window.API_BASE || "") + `/api/users/${userIdVal}/tickets${emailParam ? '?' + emailParam : ''}`, {
          headers: { 'ngrok-skip-browser-warning': 'true' }
        });
        const d = await res.json();
        if (d.success && d.tickets) {
          setTickets(d.tickets);
          matchedTicket = d.tickets.find(t => t.category === 'CAREER_ASSESSMENT');
        }
      }

      // If ticket already exists, open it!
      if (matchedTicket) {
        setActiveTicket(matchedTicket);
      } else {
        // Create dedicated ticket on-the-fly for this booking
        const createRes = await fetch((window.API_BASE || "") + '/api/tickets', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true'
          },
          body: JSON.stringify({
            name: user?.name || order.name || 'Candidate',
            email: user?.email || order.email || '',
            phone: user?.phone || order.phone || 'N/A',
            subject: `Career Assessment Booking${assessmentDate ? ` - ${assessmentDate}` : ''}`,
            message: `Selected Assessment Date: ${assessmentDate || 'Not specified'}\nOrder ID: ${orderTxnId}`,
            priority: 'HIGH',
            userId: user?.id,
            category: 'CAREER_ASSESSMENT',
            assessmentDate: assessmentDate
          })
        });
        const createData = await createRes.json();
        if (createData.success && createData.ticket) {
          setActiveTicket(createData.ticket);
          fetchUserTickets();
        } else {
          // Fallback ticket object to at least let them chat
          setActiveTicket({
            id: createData.ticketId || Date.now(),
            subject: `Career Assessment Booking${assessmentDate ? ` - ${assessmentDate}` : ''}`,
            status: 'OPEN',
            category: 'CAREER_ASSESSMENT'
          });
        }
      }
    } catch (err) {
      console.error('Failed to initialize order chat:', err);
    } finally {
      setIsOpeningChat(false);
    }
  };

  const handleSendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isSendingMessage || !activeTicket) return;

    const messageText = chatInput.trim();
    setChatInput('');
    setIsSendingMessage(true);

    try {
      const response = await fetch((window.API_BASE || "") + `/api/tickets/${activeTicket.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({
          sender_type: 'USER',
          sender_name: user?.name || 'Student',
          sender_id: user?.id,
          message: messageText
        })
      });
      const data = await response.json();
      if (data.success) {
        fetchChatMessages(activeTicket.id, true);
        fetchUserTickets();
      }
    } catch (err) {
      console.error('Failed to send chat message:', err);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || 'PENDING').toUpperCase();
    if (s === 'SUCCESS' || s === 'PAID' || s === 'COMPLETED') {
      return (
        <span className="bg-emerald-500/10 text-emerald-600 text-[11px] font-extrabold uppercase px-3 py-1 rounded-full tracking-wider border border-emerald-200">
          SUCCESS
        </span>
      );
    }
    if (s === 'PENDING' || s === 'INITIATED') {
      return (
        <span className="bg-amber-500/10 text-amber-600 text-[11px] font-extrabold uppercase px-3 py-1 rounded-full tracking-wider border border-amber-200">
          PENDING
        </span>
      );
    }
    return (
      <span className="bg-rose-500/10 text-rose-600 text-[11px] font-extrabold uppercase px-3 py-1 rounded-full tracking-wider border border-rose-200">
        FAILED
      </span>
    );
  };

  const getTitle = (order) => {
    if (order.courseName) return order.courseName;
    if (order.productName) return order.productName;
    if (order.courseId === 'CAREER_ASSESSMENT') return 'Career Assessment';
    if (order.courseId) return order.courseId.replace(/_/g, ' ');
    if (order.productType) return order.productType.replace(/_/g, ' ');
    return 'Order #' + (order.id || '');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">My Orders & Payments</h1>
        <p className="text-sm text-slate-500 mt-1">View your order details, payment status, and transaction receipts.</p>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4 text-slate-400">
            <Package size={24} />
          </div>
          <h3 className="text-slate-800 font-semibold mb-1">No orders or payment records found</h3>
          <p className="text-slate-500 text-sm">You have not placed any orders yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {orders.map((order) => {
            const orderId = order.id || order._id || order.transactionId;
            const status = (order.status || 'PENDING').toUpperCase();
            const title = getTitle(order);
            const formattedDate = order.createdAt
              ? new Date(order.createdAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : 'N/A';

            const formattedAssessmentDate = order.assessmentDate || order.assessment_date
              ? (() => {
                  try {
                    const raw = order.assessmentDate || order.assessment_date;
                    const d = new Date(raw);
                    return isNaN(d.getTime())
                      ? raw
                      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  } catch (e) {
                    return order.assessmentDate || order.assessment_date;
                  }
                })()
              : null;

            return (
              <div
                key={orderId}
                className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4"
              >
                {/* Header: Product Title & Status */}
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white shrink-0">
                      <CreditCard size={20} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900 leading-snug">
                        {title}
                      </h3>
                      <p className="text-xs text-slate-400 font-medium">
                        {order.method || 'Online (PhonePe Gateway)'}
                      </p>
                    </div>
                  </div>
                  <div>{getStatusBadge(status)}</div>
                </div>

                {/* Grid Fields */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    <div className="flex items-center gap-1.5 text-slate-400 font-medium mb-1">
                      <IndianRupee size={13} />
                      <span>Payment Amount</span>
                    </div>
                    <span className="text-sm font-extrabold text-slate-900">
                      ₹{Number(order.amount || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    <div className="flex items-center gap-1.5 text-slate-400 font-medium mb-1">
                      <Calendar size={13} />
                      <span>Payment Date</span>
                    </div>
                    <span className="text-xs font-semibold text-slate-800">
                      {formattedDate}
                    </span>
                  </div>

                  {/* Scheduled Assessment Date */}
                  {formattedAssessmentDate && (
                    <div className="col-span-2 bg-blue-50/80 p-2.5 rounded-xl border border-blue-100/90 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-blue-700 font-semibold">
                        <Calendar size={13} className="text-blue-600" />
                        <span>Assessment Date</span>
                      </div>
                      <span className="text-xs font-extrabold text-blue-900 bg-white px-2.5 py-0.5 rounded-lg border border-blue-200/60 shadow-2xs">
                        {formattedAssessmentDate}
                      </span>
                    </div>
                  )}

                  <div className="col-span-2 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    <div className="flex items-center gap-1.5 text-slate-400 font-medium mb-1">
                      <Hash size={13} />
                      <span>Payment ID / Transaction ID</span>
                    </div>
                    <span className="font-mono text-xs font-semibold text-slate-800 break-all select-all">
                      {order.transactionId || order.merchant_transaction_id || `ORD-${order.id}`}
                    </span>
                  </div>
                </div>

                {/* Action: Open Support Chat with Instructor Modal directly right here */}
                <div className="pt-2">
                  <button
                    onClick={() => handleOpenOrderChat(order)}
                    disabled={isOpeningChat}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2.5 px-4 rounded-xl text-xs font-bold transition-all shadow-xs hover:shadow-md active:scale-98 disabled:opacity-50"
                  >
                    {isOpeningChat ? (
                      <Loader2 className="animate-spin w-4 h-4" />
                    ) : (
                      <MessageCircle size={15} />
                    )}
                    <span>Chat with Instructor / Support & View Replies</span>
                  </button>
                </div>

                {/* Footer badge */}
                <div className="pt-2 flex items-center justify-between text-[11px] text-slate-400 font-medium border-t border-slate-100">
                  <div className="flex items-center gap-1.5 text-emerald-600">
                    <ShieldCheck size={14} />
                    <span>Verified Payment</span>
                  </div>
                  <span>{order.courseId || 'CAREER_ASSESSMENT'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Direct In-Page Chat Modal Drawer */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {activeTicket && (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center p-3 sm:p-6">
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                onClick={() => setActiveTicket(null)}
                className="absolute inset-0 bg-slate-950/75 backdrop-blur-md"
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 20 }} 
                animate={{ opacity: 1, scale: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 flex flex-col h-[85vh] z-10"
              >
                {/* Chat Header */}
                <div className="p-5 border-b border-slate-100 bg-white flex items-center justify-between shrink-0 shadow-2xs">
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setActiveTicket(null)} 
                      className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-900 transition-colors"
                    >
                      <ArrowLeft size={18} />
                    </button>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base leading-snug line-clamp-1">{activeTicket.subject}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          Ticket #{activeTicket.id}
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase ${
                          activeTicket.status === 'OPEN' ? 'bg-amber-50 text-amber-600 border border-amber-200/80' :
                          activeTicket.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-600 border border-blue-200/80' :
                          'bg-emerald-50 text-emerald-600 border border-emerald-200/80'
                        }`}>
                          {activeTicket.status || 'OPEN'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={() => setActiveTicket(null)}
                    className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Chat Message History */}
                <div className="flex-1 p-6 overflow-y-auto space-y-4 bg-slate-50/70">
                  {isChatLoading && chatMessages.length === 0 ? (
                    <div className="flex justify-center items-center h-full">
                      <Loader2 className="animate-spin text-blue-600 w-6 h-6" />
                    </div>
                  ) : chatMessages.length === 0 ? (
                    <div className="text-center py-10 text-slate-400 text-xs font-medium">
                      No messages in this conversation yet. Send your message to your instructor below.
                    </div>
                  ) : (
                    chatMessages.map((msg, index) => {
                      const isUser = msg.sender_type === 'USER';
                      return (
                        <div 
                          key={msg.id || index} 
                          className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                        >
                          <div className="flex items-center gap-2 mb-1 px-1">
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                              {isUser ? 'You' : (msg.sender_name || 'Instructor / Support')}
                            </span>
                            <span className="text-[9px] text-slate-400">
                              {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <div 
                            className={`min-w-[70px] max-w-[82%] px-4 py-2.5 rounded-2xl text-xs font-medium leading-relaxed whitespace-pre-wrap ${
                              isUser 
                                ? 'bg-blue-600 text-white rounded-tr-xs shadow-xs text-right' 
                                : 'bg-white text-slate-800 border border-slate-200/80 rounded-tl-xs shadow-xs text-left'
                            }`}
                          >
                            {msg.message}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Chat Input Footer */}
                <div className="border-t border-slate-100 bg-white shrink-0">
                  {activeTicket && (activeTicket.status?.toUpperCase() === 'RESOLVED' || activeTicket.status?.toUpperCase() === 'CLOSED') && (
                    <div className="px-4 py-2 bg-emerald-50/80 border-b border-emerald-100/70 flex items-center justify-between text-xs text-emerald-800">
                      <span className="flex items-center gap-1.5 font-bold">
                        <CheckCircle2 size={14} className="text-emerald-600" /> Ticket marked answered / resolved
                      </span>
                      <span className="text-[11px] text-slate-500 font-medium">Type below to reply or ask follow-up questions</span>
                    </div>
                  )}
                  <form onSubmit={handleSendChatMessage} className="p-4 flex items-center gap-3">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Type your message or query to instructor here..."
                      className="flex-1 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200/80 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-xs font-medium text-slate-800 placeholder-slate-400 transition-all shadow-2xs"
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || isSendingMessage}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl font-bold transition-all disabled:opacity-40 active:scale-95 shadow-sm shrink-0 flex items-center gap-1.5 text-xs"
                    >
                      {isSendingMessage ? <Loader2 className="animate-spin w-4 h-4" /> : <><Send size={15} /><span>Send</span></>}
                    </button>
                  </form>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default UserOrders;
